import { ReactNode, useId } from 'react';
import { Area, AreaChart, CartesianGrid, Line, XAxis, YAxis } from 'recharts';

import { cn } from '@/lib/utils';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { formatCompact } from '../admin/format';

export interface TrendPoint {
  /** Axis label: a date string or a short month name. */
  label: string;
  value: number;
  /** The same point in the previous period, drawn dashed. */
  previous?: number | null;
}

/**
 * One series over time, with the previous period dashed behind it when there
 * is one — so "is this good?" is answered on the chart itself.
 */
export function TrendChart({
  data,
  valueLabel,
  previousLabel,
  height = 220,
  formatValue = (v) => formatCompact(v),
  formatLabel,
  partialLast,
  partialLabel,
  className,
  testId,
}: {
  data: TrendPoint[];
  /**
   * The last point is a period still under way (today, this week): drawn as
   * a dotted ink stub to a hollow dot, so a half-finished day does not read
   * as a collapse.
   */
  partialLast?: boolean;
  /** Tooltip name for that point ("So far"). */
  partialLabel?: string;
  valueLabel: string;
  previousLabel?: string;
  height?: number;
  formatValue?: (v: number) => string;
  formatLabel?: (label: string) => string;
  className?: string;
  testId?: string;
}) {
  const id = useId().replace(/:/g, '');
  const hasPrevious = data.some((d) => d.previous != null);
  const split = Boolean(partialLast) && data.length >= 2;
  const rows = split
    ? data.map((d, i) => ({
        ...d,
        value: i < data.length - 1 ? d.value : null,
        partial: i >= data.length - 2 ? d.value : null,
      }))
    : data;
  const config: ChartConfig = {
    value: { label: valueLabel, color: 'var(--foreground)' },
    previous: { label: previousLabel ?? '', color: 'var(--muted-foreground)' },
    partial: { label: partialLabel ?? valueLabel, color: 'var(--foreground)' },
  };

  return (
    <ChartContainer config={config} className={cn('aspect-auto w-full', className)} style={{ height }} data-testid={testId}>
      <AreaChart data={rows} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`trend-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--foreground)" stopOpacity={0.1} />
            <stop offset="100%" stopColor="var(--foreground)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={formatLabel}
          className="text-xs"
        />
        <YAxis tickLine={false} axisLine={false} width={40} tickFormatter={(v) => formatValue(Number(v))} className="text-xs" />
        <ChartTooltip
          cursor={false}
          content={(props: any) => (
            <ChartTooltipContent
              {...props}
              // The stub shares the last whole point with the main line; say it once.
              payload={(props.payload || []).filter((p: any, _: number, all: any[]) => !(p.dataKey === 'partial' && all.some((o) => o.dataKey === 'value' && o.value != null)))}
              labelFormatter={formatLabel ? (l: string) => formatLabel(String(l)) : undefined}
            />
          )}
        />
        {hasPrevious && (
          <Line dataKey="previous" type="monotone" stroke="var(--muted-foreground)" strokeDasharray="4 4" strokeWidth={1.25} dot={false} isAnimationActive={false} />
        )}
        <Area dataKey="value" type="monotone" stroke="var(--foreground)" strokeWidth={1.75} fill={`url(#trend-${id})`} isAnimationActive={false} />
        {split && (
          <Line
            dataKey="partial"
            type="monotone"
            stroke="var(--foreground)"
            strokeDasharray="2 3"
            strokeWidth={1.5}
            connectNulls={false}
            isAnimationActive={false}
            dot={(p: any) =>
              p.index === rows.length - 1 ? <circle key="partial-end" cx={p.cx} cy={p.cy} r={3} fill="var(--background)" stroke="var(--foreground)" strokeWidth={1.5} /> : <g key={`partial-${p.index}`} />
            }
          />
        )}
      </AreaChart>
    </ChartContainer>
  );
}

/** A small labelled figure inside a card: "Avg. position 14.2". */
export function MiniStat({
  label,
  value,
  sub,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <p className="truncate text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-xl leading-none font-medium tracking-tight tabular-nums">{value}</p>
      {sub && <p className="mt-1 truncate text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}
